import { useState, useCallback, useRef } from 'react';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import { API_ENDPOINTS } from '../../config/constants';

// ─── CSV Parser (mirrors server-side logic) ───────────────────────────────────

function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

const HEADER_MAP = {
  'lead_id': 'external_lead_id',
  'created_time': 'created_time',
  'created_date': 'created_date',
  'form_id': 'form_id',
  'form_name': 'form_name',
  'test_lead': 'test_lead',
  'email address': 'email',
  'last name': 'last_name',
  'country/region': 'country',
  'company name': 'company_name',
  'job title': 'job_title',
  'first name': 'first_name',
};

function normalizeHeader(h) {
  const lower = h.toLowerCase().trim();
  if (lower.startsWith('the event organizer')) return 'marketing_consent';
  return HEADER_MAP[lower] || lower;
}

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n');
  if (lines.length < 2) return { headers: [], rows: [] };
  const rawHeaders = parseCSVLine(lines[0]);
  const headers = rawHeaders.map(normalizeHeader);
  const rows = lines.slice(1)
    .filter(l => l.trim())
    .map(line => {
      const values = parseCSVLine(line);
      const row = {};
      headers.forEach((h, i) => { row[h] = (values[i] || '').trim(); });
      return row;
    });
  return { headers, rows };
}

// ─── Email templates for event registration leads ─────────────────────────────

const EMAIL_TEMPLATES = [
  { level: 1, template: 'copilot_welcome',        subject: 'Thank you for registering — here\'s what\'s next' },
  { level: 2, template: 'copilot_details_sent',   subject: 'Event details & joining instructions' },
  { level: 3, template: 'copilot_followup_1',     subject: 'Reminder: Your session is coming up' },
  { level: 4, template: 'copilot_followup_2',     subject: 'Don\'t miss out — limited seats remaining' },
  { level: 5, template: 'copilot_final_followup', subject: 'Last chance — event starts soon' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function EventRegistrations() {
  const [tab, setTab] = useState('import'); // 'import' | 'view'

  // Import tab state
  const [csvText, setCsvText] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { inserted, skipped, errors, total }
  const [importError, setImportError] = useState('');
  const fileInputRef = useRef(null);

  // View tab state
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [regError, setRegError] = useState('');
  const [formIdFilter, setFormIdFilter] = useState('');
  const [viewPage, setViewPage] = useState(1);
  const ROWS_PER_PAGE = 20;

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // the registration row
  const [selectedTemplate, setSelectedTemplate] = useState(EMAIL_TEMPLATES[0].template);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentFor, setEmailSentFor] = useState(null); // registration id last emailed

  // ── File handling ────────────────────────────────────────────────────────────

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setImportError('Please select a .csv file.');
      return;
    }
    setImportError('');
    setImportResult(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setCsvText(text);
      const { rows } = parseCSV(text);
      setParsedRows(rows);
    };
    reader.readAsText(file);
  }, []);

  const handleFileInput = (e) => handleFile(e.target.files[0]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const clearFile = () => {
    setCsvText('');
    setParsedRows([]);
    setFileName('');
    setImportResult(null);
    setImportError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Import API call ──────────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!csvText) return;
    setImporting(true);
    setImportError('');
    setImportResult(null);
    try {
      const res = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/event-registrations/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/csv',
          [TOKEN_HEADER]: generateToken(),
        },
        body: csvText,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setImportError(data.message || 'Import failed.');
      } else {
        setImportResult(data.data);
        clearFile();
      }
    } catch (err) {
      setImportError('Network error: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  // ── View API call ────────────────────────────────────────────────────────────

  const fetchRegistrations = async () => {
    setLoadingRegs(true);
    setRegError('');
    setViewPage(1);
    try {
      const params = new URLSearchParams({ limit: 1000, offset: 0 });
      if (formIdFilter.trim()) params.set('formId', formIdFilter.trim());
      const res = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/event-registrations?${params}`, {
        headers: { [TOKEN_HEADER]: generateToken() },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load');
      setRegistrations(data.data);
    } catch (err) {
      setRegError(err.message);
    } finally {
      setLoadingRegs(false);
    }
  };

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'view' && registrations.length === 0) fetchRegistrations();
  };

  // ── Email handlers ───────────────────────────────────────────────────────────

  const openEmailModal = (reg) => {
    setEmailTarget(reg);
    setSelectedTemplate(EMAIL_TEMPLATES[0].template);
    setShowEmailModal(true);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEmailTarget(null);
  };

  const handleSendEmail = async () => {
    if (!emailTarget) return;
    const tpl = EMAIL_TEMPLATES.find(t => t.template === selectedTemplate);
    setSendingEmail(true);
    try {
      const name = [emailTarget.first_name, emailTarget.last_name].filter(Boolean).join(' ') || emailTarget.email;
      const res = await fetch(`${API_ENDPOINTS.LEADS_ADMIN_SEND_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify({
          leadid: emailTarget.id,
          email: emailTarget.email,
          name,
          level: tpl?.level ?? 1,
          programkey: 'architect_program',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to send email');
      setEmailSentFor(emailTarget.id);
      closeEmailModal();
      alert(`Email sent successfully to ${emailTarget.email}`);
    } catch (err) {
      alert(`Failed to send email: ${err.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Export helper ────────────────────────────────────────────────────────────

  const exportToCSV = () => {
    if (!registrations.length) return;
    const headers = ['id','email','first_name','last_name','company_name','job_title','country',
      'form_name','form_id','created_date','test_lead','marketing_consent','imported_at','imported_by'];
    const rows = registrations.map(r =>
      headers.map(h => {
        const v = r[h];
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    );
    const blob = new Blob([headers.join(',') + '\n' + rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Pagination ───────────────────────────────────────────────────────────────

  const totalViewPages = Math.ceil(registrations.length / ROWS_PER_PAGE);
  const pagedRegs = registrations.slice((viewPage - 1) * ROWS_PER_PAGE, viewPage * ROWS_PER_PAGE);

  // ── Unique form names for the preview table summary ──────────────────────────

  const uniqueForms = [...new Set(parsedRows.map(r => r.form_name).filter(Boolean))];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="section-content">
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
        {[
          { key: 'import', label: '📥 Import CSV' },
          { key: 'view',   label: '📋 View Registrations' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleTabChange(key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: tab === key ? '#dc2626' : '#6b7280',
              borderBottom: tab === key ? '2px solid #dc2626' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'color 0.2s',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── IMPORT TAB ──────────────────────────────────────────────────────── */}
      {tab === 'import' && (
        <div>
          {/* Import result banner */}
          {importResult && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px',
              padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <div>
                <strong style={{ color: '#15803d' }}>Import successful</strong>
                <div style={{ fontSize: '13px', color: '#166534', marginTop: '4px' }}>
                  {importResult.total} rows processed &nbsp;·&nbsp;
                  <span style={{ color: '#15803d', fontWeight: 600 }}>{importResult.inserted} inserted</span>
                  &nbsp;·&nbsp;
                  <span style={{ color: '#92400e' }}>{importResult.skipped} skipped (duplicates)</span>
                  {importResult.errors?.length > 0 && (
                    <span style={{ color: '#dc2626' }}>&nbsp;·&nbsp;{importResult.errors.length} errors</span>
                  )}
                </div>
              </div>
              {importResult.errors?.length > 0 && (
                <details style={{ fontSize: '12px', marginTop: '8px', width: '100%' }}>
                  <summary style={{ cursor: 'pointer', color: '#dc2626' }}>Show errors</summary>
                  <ul style={{ marginTop: '8px', paddingLeft: '16px' }}>
                    {importResult.errors.map((e, i) => (
                      <li key={i}><strong>{e.email}</strong>: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Instructions card */}
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px',
            padding: '14px 18px', marginBottom: '20px', fontSize: '13px', color: '#1e40af'
          }}>
            <strong>How to import:</strong> Export leads from LinkedIn Lead Gen Forms (or similar platform) as CSV.
            Drop the file below or click to browse. Duplicate entries (same email + form) are automatically skipped.
          </div>

          {/* Drop zone */}
          {!fileName ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragging ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '12px',
                padding: '48px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: isDragging ? '#fef2f2' : '#fafafa',
                transition: 'all 0.2s',
                marginBottom: '20px',
              }}
            >
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📂</div>
              <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#374151' }}>
                Drag &amp; drop your CSV file here
              </p>
              <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                or <span style={{ color: '#dc2626', fontWeight: 600 }}>click to browse</span> &nbsp;·&nbsp; .csv files only
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            /* File loaded — show preview */
            <div>
              {/* File info bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: '8px', padding: '12px 16px', marginBottom: '16px'
              }}>
                <span style={{ fontSize: '20px' }}>📄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>{fileName}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {parsedRows.length} rows parsed
                    {uniqueForms.length > 0 && ` · ${uniqueForms.length} form(s): ${uniqueForms.slice(0, 2).join(', ')}${uniqueForms.length > 2 ? '…' : ''}`}
                  </div>
                </div>
                <button onClick={clearFile} style={{
                  background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px',
                  padding: '4px 10px', cursor: 'pointer', color: '#6b7280', fontSize: '12px'
                }}>✕ Clear</button>
              </div>

              {/* Preview table */}
              {parsedRows.length > 0 && (
                <div className="table-container" style={{ marginBottom: '16px', maxHeight: '320px', overflowY: 'auto' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Email</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Company</th>
                        <th>Job Title</th>
                        <th>Country</th>
                        <th>Form Name</th>
                        <th>Test?</th>
                        <th>Consent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, i) => (
                        <tr key={i}>
                          <td style={{ color: '#9ca3af', fontSize: '12px' }}>{i + 1}</td>
                          <td>{row.email || <span style={{ color: '#dc2626' }}>missing</span>}</td>
                          <td>{row.first_name || '—'}</td>
                          <td>{row.last_name || '—'}</td>
                          <td>{row.company_name || '—'}</td>
                          <td>{row.job_title || '—'}</td>
                          <td>{row.country || '—'}</td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={row.form_name}>{row.form_name || '—'}</td>
                          <td>
                            <span className={`badge ${row.test_lead === 'TRUE' ? 'badge-warning' : 'badge-success'}`}>
                              {row.test_lead === 'TRUE' ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${row.marketing_consent === 'TRUE' ? 'badge-success' : 'badge-danger'}`}>
                              {row.marketing_consent === 'TRUE' ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Error */}
              {importError && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px',
                  padding: '10px 14px', color: '#dc2626', fontSize: '13px', marginBottom: '12px'
                }}>
                  ⚠️ {importError}
                </div>
              )}

              {/* Import button */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="action-btn action-btn-primary"
                  onClick={handleImport}
                  disabled={importing || parsedRows.length === 0}
                >
                  {importing ? '⏳ Importing…' : `📥 Import ${parsedRows.length} Row${parsedRows.length !== 1 ? 's' : ''}`}
                </button>
                <button className="action-btn action-btn-secondary" onClick={clearFile} disabled={importing}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Error when no file loaded */}
          {!fileName && importError && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px',
              padding: '10px 14px', color: '#dc2626', fontSize: '13px', marginTop: '12px'
            }}>
              ⚠️ {importError}
            </div>
          )}
        </div>
      )}

      {/* ── VIEW TAB ────────────────────────────────────────────────────────── */}
      {tab === 'view' && (
        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Filter by Form ID…"
              value={formIdFilter}
              onChange={e => setFormIdFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchRegistrations()}
              style={{
                padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px',
                fontSize: '14px', width: '220px', outline: 'none'
              }}
            />
            <button className="action-btn action-btn-primary" onClick={fetchRegistrations} disabled={loadingRegs}>
              {loadingRegs ? '⏳ Loading…' : '🔍 Search'}
            </button>
            <button className="action-btn action-btn-secondary" onClick={() => { setFormIdFilter(''); fetchRegistrations(); }} disabled={loadingRegs}>
              🔄 Refresh
            </button>
            {registrations.length > 0 && (
              <button className="action-btn action-btn-secondary" onClick={exportToCSV}>
                ⬇️ Export CSV
              </button>
            )}
            {registrations.length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7280' }}>
                {registrations.length} record{registrations.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {regError && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px',
              padding: '10px 14px', color: '#dc2626', fontSize: '13px', marginBottom: '16px'
            }}>
              ⚠️ {regError}
            </div>
          )}

          {!loadingRegs && registrations.length === 0 && !regError && (
            <div style={{ textAlign: 'center', padding: '60px 24px', color: '#9ca3af' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
              <p style={{ margin: 0 }}>No registrations found. Try importing a CSV file first.</p>
            </div>
          )}

          {registrations.length > 0 && (
            <>
              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Job Title</th>
                      <th>Country</th>
                      <th>Form</th>
                      <th>Date</th>
                      <th>Consent</th>
                      <th>Test</th>
                      <th>Imported At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRegs.map(r => (
                      <tr key={r.id}>
                        <td style={{ color: '#9ca3af', fontSize: '12px' }}>{r.id}</td>
                        <td>{r.email}</td>
                        <td>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                        <td>{r.company_name || '—'}</td>
                        <td>{r.job_title || '—'}</td>
                        <td>{r.country || '—'}</td>
                        <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={r.form_name}>{r.form_name || '—'}</td>
                        <td>{r.created_date ? new Date(r.created_date).toLocaleDateString() : '—'}</td>
                        <td>
                          <span className={`badge ${r.marketing_consent ? 'badge-success' : 'badge-danger'}`}>
                            {r.marketing_consent ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          {r.test_lead
                            ? <span className="badge badge-warning">Test</span>
                            : <span className="badge badge-info">Real</span>
                          }
                        </td>
                        <td style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {new Date(r.imported_at).toLocaleString()}
                        </td>
                        <td>
                          <button
                            className="action-btn action-btn-email"
                            onClick={() => openEmailModal(r)}
                            title={`Send email to ${r.email}`}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {emailSentFor === r.id ? '✅ Sent' : '✉️ Send Email'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalViewPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px', alignItems: 'center' }}>
                  <button
                    className="action-btn action-btn-secondary"
                    onClick={() => setViewPage(p => Math.max(1, p - 1))}
                    disabled={viewPage === 1}
                  >← Prev</button>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>
                    Page {viewPage} of {totalViewPages}
                  </span>
                  <button
                    className="action-btn action-btn-secondary"
                    onClick={() => setViewPage(p => Math.min(totalViewPages, p + 1))}
                    disabled={viewPage === totalViewPages}
                  >Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── SEND EMAIL MODAL ──────────────────────────────────────────────── */}
      {showEmailModal && emailTarget && (
        <div className="modal-overlay" onClick={closeEmailModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Email</h2>
              <button className="modal-close" onClick={closeEmailModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-lead-info">
                <strong>To:</strong>{' '}
                {[emailTarget.first_name, emailTarget.last_name].filter(Boolean).join(' ') || '—'}
                {' '}({emailTarget.email})
              </p>
              {emailTarget.company_name && (
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>
                  {emailTarget.job_title ? `${emailTarget.job_title}, ` : ''}{emailTarget.company_name}
                </p>
              )}
              <div className="form-group">
                <label htmlFor="emailTemplateSelect">Select Email Template:</label>
                <select
                  id="emailTemplateSelect"
                  value={selectedTemplate}
                  onChange={e => setSelectedTemplate(e.target.value)}
                  className="status-select"
                  autoFocus
                >
                  {EMAIL_TEMPLATES.map(t => (
                    <option key={t.template} value={t.template}>
                      Level {t.level}: {t.subject}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={closeEmailModal} disabled={sendingEmail}>
                Cancel
              </button>
              <button className="modal-btn modal-btn-confirm" onClick={handleSendEmail} disabled={sendingEmail}>
                {sendingEmail ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
